import React, { useEffect, useState } from "react";
import { Row, Col, FormControl, FormGroup } from "react-bootstrap";
import { useParams, useHistory } from "react-router-dom";
import DocumentUploadCard from "../../../components/CustomControls/DocumentUpload/DocumentUploadCard";
import { Card } from "../../../components/Cards/Card";
import { useTranslation } from "react-i18next";
import { Back, IconSVG, Save } from "../../../components/Icons/Icons";
import { CustomSelect } from "../../../components/CustomControls/CustomSelect";
import {
  getRegulatoryEvent,
  saveRegulatoryEvent,
  uploadAttachmentMetadata,
} from "../../../api/clinicalTrial";
import { getProtocol } from "../../../api/protocol";
import { Spinner } from "../../../components/Utils/Loaders";
import { dateFormatter } from "../../../components/Utils/Formatter";
import {
  attachmentEntityType,
  regulatorySubmissionTypes,
  regulatorySubmissionStatuses,
} from "../../../variables/Enums";
import { ModalEditRegulatoryEvent } from "../../../components/ClinicalTrials/Regulatory/ModalEditRegulatoryEvent";
import { ModalCreateRegulatoryCategory } from "../../../components/ClinicalTrials/Regulatory/ModalCreateRegulatoryCategory";
import { CustomRadio } from "../../../components/CustomControls/CustomRadio";
import { useAuthorization } from "../../../components/Authorization/useAuthorization";
import {
  AttachmentSlotPollingProvider,
  useAttachmentSlotPolling,
} from "../../../components/CustomControls/DocumentUpload/hooks/AttachmentSlotPollingProvider";

const RegulatoryFilesContent = () => {
  const { t } = useTranslation();
  const [, user, isAuthorized] = useAuthorization();
  const navigation = useHistory();
  const { id } = useParams();
  const [regulatorySubmission, setRegulatorySubmission] = useState();
  const [systemStatusesOptions, setSystemStatusesOptions] = useState();
  const [actualStatusRecord, setActualStatusRecord] = useState();
  const [lastStatusRecordId, setLastStatusRecordId] = useState();
  const [oldObservation, setOldObservation] = useState();
  const [saving, setSaving] = useState();
  const [triggerSave, setTriggerSave] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);

  const polling = useAttachmentSlotPolling();
  const setCurrentUserFromAuth = polling?.setCurrentUserFromAuth;
  const setUsersFromProtocolPractitioners =
    polling?.setUsersFromProtocolPractitioners;

  useEffect(() => {
    if (!setCurrentUserFromAuth) return;
    setCurrentUserFromAuth(user);
  }, [setCurrentUserFromAuth, user]);

  useEffect(() => {
    if (!setUsersFromProtocolPractitioners) return;
    if (!regulatorySubmission?.protocolId) return;

    let isCancelled = false;

    const loadProtocolUsers = async () => {
      try {
        const result = await getProtocol(regulatorySubmission.protocolId);
        if (isCancelled) return;

        if (result?.status === 200) {
          const protocolPractitioners =
            result?.data?.protocol?.protocolPractitioners ||
            result?.data?.protocolPractitioners ||
            [];
          setUsersFromProtocolPractitioners(protocolPractitioners);
        }
      } catch (error) {
        console.error("Error al cargar usuarios del protocolo:", error);
      }
    };

    loadProtocolUsers();

    return () => {
      isCancelled = true;
    };
  }, [
    setUsersFromProtocolPractitioners,
    regulatorySubmission?.protocolId,
  ]);

  useEffect(() => {
    if (triggerSave) {
      save();
      setTriggerSave(false);
    }
  }, [triggerSave]);

  const loadData = async () => {
    const result = await getRegulatoryEvent(id);

    if (result.status === 200) {
      setRegulatorySubmission(result.data);

      var systemStatuses = result.data.systemStatuses.map((x) => ({
        value: x.id,
        label: x.name,
      }));
      setSystemStatusesOptions(systemStatuses);

      const regulatorySubmissionStatuses = result.data.statuses;
      const currentStatus =
        regulatorySubmissionStatuses[regulatorySubmissionStatuses.length - 1];

      setActualStatusRecord(currentStatus);
      setLastStatusRecordId(currentStatus.id);
    }
  };

  const getAttachmentEntityId = (att) => {
    switch (att.attachmentEntityTypeId) {
      case attachmentEntityType.Startup:
        return regulatorySubmission.startupId;
      case attachmentEntityType.Practitioner:
        return att.practitionerId;
      case attachmentEntityType.Protocols:
        return regulatorySubmission.protocolId;
      case attachmentEntityType.RegulatorySubmissions:
        return regulatorySubmission.id;
      case attachmentEntityType.Hospital:
      default:
        return undefined;
    }
  };

  const save = async () => {
    const regulatorySubmissionStatuses = regulatorySubmission.statuses;
    const currentStatus =
      regulatorySubmissionStatuses[regulatorySubmissionStatuses.length - 1];

    const patientPayload =
      regulatorySubmission.patients?.length > 0
        ? regulatorySubmission.patients.map((p) => ({
            medicalRecordNumber: p.medicalRecordNumber,
            numberInProtocol: p.numberInProtocol,
          }))
        : regulatorySubmission.medicalRecordNumber
          ? [
              {
                medicalRecordNumber: regulatorySubmission.medicalRecordNumber,
                numberInProtocol: regulatorySubmission.numberInProtocol,
              },
            ]
          : [];

    const data = {
      name: regulatorySubmission.name,
      observation: actualStatusRecord.observation,
      requiresConsent: actualStatusRecord.requiresConsent,
      statusId: actualStatusRecord.statusId,
      patients: patientPayload,
      medicalRecordNumber: patientPayload[0]?.medicalRecordNumber ?? null,
      numberInProtocol: patientPayload[0]?.numberInProtocol ?? null,
      protocolAttachments:
        currentStatus.attachments
          ?.filter(
            (x) =>
              !x.attachmentCategoryId &&
              x.attachmentEntityTypeId === attachmentEntityType.Protocols
          )
          .map((att) => att.id) ?? [],
      attachmentsCategories:
        currentStatus?.attachments
          ?.filter((x) => !!x.attachmentCategoryId)
          .map((att) => ({
            id: att.attachmentCategoryId,
            groupId: att.groupId ?? null,
            practitionerId: att.practitionerId ?? null,
            attachmentId: att.id ?? null,
          })) ?? [],
    };

    try {
      setSaving(true);
      const result = await saveRegulatoryEvent(
        regulatorySubmission.protocolId,
        regulatorySubmission.id,
        data
      );

      if (result.status === 200) {
        loadData();
      }
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!id || !!regulatorySubmission) {
      return;
    }

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, regulatorySubmission]);

  const handleStatusRecordClick = (statusRecordId) => {
    const currentStatus = regulatorySubmission.statuses.find(
      (x) => x.id === statusRecordId
    );
    setActualStatusRecord(currentStatus);
  };

  const onChangeFilter = (_, option) => {
    const newActualStatus = {
      ...actualStatusRecord,
      observation: "",
      statusId: option.value,
    };
    if (option.value !== actualStatusRecord.statusId) {
      setOldObservation(actualStatusRecord.observation);
      newActualStatus.observation = "";
    } else {
      newActualStatus.observation = oldObservation;
    }

    setActualStatusRecord(newActualStatus);
  };

  const handleStatusFieldChange = (e) => {
    const { name, type, checked, value } = e.currentTarget;
    const newValue = type === "checkbox" ? checked : value;
    const newActualStatus = {
      ...actualStatusRecord,
      [name]: newValue,
    };

    setActualStatusRecord(newActualStatus);
  };

  const handleAttachmentChange = async (newAttachment) => {
    // Verificar si se subió un archivo (tiene ID y nombre de archivo)
    const wasFileUploaded =
      newAttachment.id && newAttachment.attachmentFileName;

    // Verificar si el attachment ya existía antes
    const existingAttachment = actualStatusRecord.attachments.find((att) => {
      const isCategorized = !!att.attachmentCategoryId;
      const isSameAttachment = isCategorized
        ? att.attachmentCategoryId === newAttachment.attachmentCategoryId &&
          att.groupId === newAttachment.groupId &&
          att.practitionerId === newAttachment.practitionerId &&
          att.medicalRecordNumber === newAttachment.medicalRecordNumber
        : att.id === newAttachment.id;
      return isSameAttachment;
    });

    // Si hay metadata modificada y el attachment tiene ID, guardar la metadata
    if (
      newAttachment?.id &&
      newAttachment?.extendedMetadata &&
      newAttachment?.attachmentEntityTypeId
    ) {
      try {
        await uploadAttachmentMetadata(
          newAttachment.attachmentEntityTypeId,
          newAttachment.id,
          newAttachment.extendedMetadata
        );
      } catch (err) {
        console.error("Error al actualizar metadata:", err);
      }
    }

    const updatedStatusRecord = {
      ...actualStatusRecord,
      attachments: actualStatusRecord.attachments.map((att) => {
        const isCategorized = !!att.attachmentCategoryId;

        const isSameAttachment = isCategorized
          ? att.attachmentCategoryId === newAttachment.attachmentCategoryId &&
            att.groupId === newAttachment.groupId &&
            att.practitionerId === newAttachment.practitionerId &&
            att.medicalRecordNumber === newAttachment.medicalRecordNumber
          : att.id === newAttachment.id;

        return isSameAttachment ? newAttachment : att;
      }),
    };

    // actualizo el estado actual
    setActualStatusRecord(updatedStatusRecord);

    // y tambien actualizo dentro de regulatorySubmission.statuses
    const updatedStatuses = regulatorySubmission.statuses.map((status) =>
      status.id === updatedStatusRecord.id ? updatedStatusRecord : status
    );

    setRegulatorySubmission({
      ...regulatorySubmission,
      statuses: updatedStatuses,
    });

    // Si se subió un archivo nuevo o se actualizó uno existente, guardar automáticamente
    // Verificamos que tenga ID y nombre de archivo, y que sea diferente del anterior
    if (wasFileUploaded) {
      const fileChanged =
        !existingAttachment ||
        existingAttachment.id !== newAttachment.id ||
        existingAttachment.attachmentFileName !==
          newAttachment.attachmentFileName;

      if (fileChanged) {
        setTriggerSave(true);
      }
    }
  };

  const groupArrayByTwo = (arr) => {
    const result = [];
    for (let i = 0; i < arr.length; i += 2) {
      result.push(arr.slice(i, i + 2));
    }
    return result;
  };

  const renderTimeline = () => {
    const statuses = regulatorySubmission?.statuses ?? [];

    let lastPresentationDate = null;

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          padding: "20px",
          overflowX: "auto",
        }}
      >
        {statuses.map((status, idx) => {
          let tiempoRespuesta = null;
          let lastPresentationStatusId = status.statusId;

          if (status.statusId === 2) {
            // Presentado al Comité
            lastPresentationDate = new Date(status.dateTime);
          }

          // si son estados que cierran la espera de respuesta
          // muestro el tiempo de demora
          if ([3, 4, 5].includes(status.statusId) && lastPresentationDate) {
            const fechaActual = new Date(status.dateTime);
            const diffMs = fechaActual - lastPresentationDate;

            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            const diffHours = Math.floor(
              (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
            );
            const diffMinutes = Math.floor(
              (diffMs % (1000 * 60 * 60)) / (1000 * 60)
            );

            let parts = [];
            if (diffDays > 0)
              parts.push(`${diffDays} día${diffDays > 1 ? "s" : ""}`);
            if (diffHours > 0) parts.push(`${diffHours} h`);
            if (diffMinutes > 0) {
              parts.push(`${diffMinutes} min`);
            } else if (diffDays === 0 && diffHours === 0 && diffMinutes === 0) {
              parts.push(t("regulatorySubmissions.item.waitingTimeLow"));
            }

            tiempoRespuesta = `${t(
              "regulatorySubmissions.item.waitingTime"
            )} ${parts.join(" ")}`;

            if (lastPresentationStatusId !== 3) {
              lastPresentationDate = null; // reseteo a proxima presentación
            }
          }

          const isLast = idx === statuses.length - 1;

          return (
            <div
              key={idx}
              style={{
                textAlign: "center",
                margin: "0 20px",
                position: "relative",
              }}
            >
              <div
                style={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  backgroundColor: "#d9edf7",
                  border: "2px solid #fff",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  margin: "0 auto",
                  zIndex: 1,
                }}
              >
                <i
                  className="glyphicon glyphicon-calendar"
                  style={{ color: "#31708f", cursor: "pointer" }}
                  onClick={() => {
                    handleStatusRecordClick(status.id);
                  }}
                ></i>
              </div>

              <div
                style={{
                  marginTop: "10px",
                  backgroundColor:
                    status.id === actualStatusRecord?.id
                      ? "#dff0d8"
                      : "#f5f5f5",
                  border: `2px solid ${
                    status.id === actualStatusRecord?.id ? "#3c763d" : "#d4d4d4"
                  }`,
                  padding: "10px",
                  borderRadius: "2px",
                  minWidth: "200px",
                }}
              >
                <h6
                  style={{ marginTop: 0, cursor: "pointer" }}
                  onClick={() => {
                    handleStatusRecordClick(status.id);
                  }}
                >
                  {status.name}
                </h6>
                <p>
                  <small style={{ color: "#999" }}>
                    {dateFormatter(
                      new Date(status.dateTime),
                      "DD/MMM/YYYY HH:mm"
                    )}
                  </small>
                </p>
                {tiempoRespuesta && (
                  <p
                    style={{
                      color: "#d9534f",
                      fontWeight: "bold",
                      fontSize: "12px",
                    }}
                  >
                    {tiempoRespuesta}
                  </p>
                )}
                <p style={{ fontSize: "12px", fontStyle: "italic" }}>
                  {status.observation}
                </p>
              </div>

              {!isLast && (
                <div
                  style={{
                    position: "absolute",
                    top: "15px",
                    right: "-40px",
                    width: "40px",
                    height: "3px",
                    backgroundColor: "#eee",
                  }}
                ></div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const onEditSave = (attachments, medicalRecordNumber, numberInProtocol) => {
    // actualizo ultimo estado y paciente
    setRegulatorySubmission((prev) => {
      const updatedStatuses = [...prev.statuses];
      const lastStatusIndex = updatedStatuses.length - 1;
      const lastStatus = {
        ...updatedStatuses[lastStatusIndex],
        attachments: [...attachments],
      };
      updatedStatuses[lastStatusIndex] = lastStatus;

      return {
        ...prev,
        statuses: updatedStatuses,
        medicalRecordNumber: medicalRecordNumber,
        numberInProtocol:
          numberInProtocol !== undefined && numberInProtocol !== null
            ? numberInProtocol
            : prev.numberInProtocol,
      };
    });
    setTriggerSave(true);
    setShowEditModal(false);
  };

  const onAddCategorySave = (id, name) => {
    const newAttachment = {
      attachmentEntityTypeId: attachmentEntityType.RegulatorySubmissions,
      attachmentCategoryId: id,
      AttachmentCategoryName: name,
    };

    // actualizo ultimo estado
    setRegulatorySubmission((prev) => {
      const updatedStatuses = [...prev.statuses];
      const lastStatusIndex = updatedStatuses.length - 1;
      const lastStatus = {
        ...updatedStatuses[lastStatusIndex],
        attachments: [
          ...(updatedStatuses[lastStatusIndex].attachments || []),
          newAttachment,
        ],
      };
      updatedStatuses[lastStatusIndex] = lastStatus;

      return {
        ...prev,
        statuses: updatedStatuses,
      };
    });

    setTriggerSave(true);
    setShowAddCategoryModal(false);
  };

  const lastStatusRecordSelected =
    lastStatusRecordId === actualStatusRecord?.id;

  return (
    <>
      {!regulatorySubmission && <Spinner />}
      {regulatorySubmission && (
        <>
          <div>
            <h3 style={{ marginTop: "0px" }}>
              #{regulatorySubmission.id} {regulatorySubmission.name}
            </h3>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginBottom: "20px" }}
            onClick={() => navigation.goBack()}
          >
            <Back /> {t("buttons.back")}
          </button>
          <Card content={renderTimeline()} />
          <Card
            content={
              <div style={{ whiteSpace: "nowrap" }}>
                <FormGroup>
                  <div
                    style={{
                      display: "inline-block",
                      verticalAlign: "middle",
                      marginRight: "24px",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "bold",
                        fontSize: "14px",
                        marginBottom: "2px",
                      }}
                    >
                      {t("regulatorySubmissions.typeLabel")}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    {[
                      regulatorySubmissionTypes.notification,
                      regulatorySubmissionTypes.approval,
                    ].map((opt) => (
                      <div
                        key={opt}
                        style={{ marginLeft: opt === 2 ? "10px" : "0" }}
                      >
                        <CustomRadio
                          number={`type-${opt}`}
                          name="type"
                          label={t(`regulatorySubmissions.types.${opt}`)}
                          option={opt}
                          checked={
                            regulatorySubmission.regulatorySubmissionTypeId ===
                            opt
                          }
                          disabled={true}
                          onChange={() => {}}
                        />
                      </div>
                    ))}
                  </div>
                </FormGroup>
                <FormGroup>
                  <div
                    style={{
                      display: "inline-block",
                      verticalAlign: "middle",
                      marginRight: "24px",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "bold",
                        fontSize: "14px",
                        marginBottom: "2px",
                      }}
                    >
                      {t("regulatorySubmissions.statusLabel")}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "inline-block",
                      verticalAlign: "middle",
                      marginRight: "8px",
                      marginLeft: "40px",
                      color: "#6c757d",
                      fontSize: "14px",
                    }}
                  >
                    {t("regulatorySubmissions.switchStateLabel")}
                  </div>

                  <div
                    style={{
                      display: "inline-block",
                      verticalAlign: "middle",
                      minWidth: "200px",
                    }}
                  >
                    <CustomSelect
                      value={systemStatusesOptions.find(
                        (x) => x.value === actualStatusRecord.statusId
                      )}
                      isDisabled={!lastStatusRecordSelected}
                      name="status"
                      onChange={onChangeFilter}
                      isMulti={false}
                      isClearable={false}
                      loading={false}
                      options={systemStatusesOptions}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.value}
                    />
                  </div>
                  {actualStatusRecord.statusId ===
                    regulatorySubmissionStatuses.implemented && (
                    <div className="form-check" style={{ marginBottom: 8 }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="chk-consent"
                        name="requiresConsent"
                        checked={actualStatusRecord.requiresConsent}
                        onChange={handleStatusFieldChange}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="chk-consent"
                        style={{ marginLeft: 6 }}
                      >
                        {t("regulatorySubmissions.requireConsentLabel")}
                      </label>
                    </div>
                  )}
                </FormGroup>
                <FormGroup>
                  <div
                    style={{
                      fontWeight: "bold",
                      fontSize: "14px",
                      marginBottom: "2px",
                    }}
                  >
                    {t("regulatorySubmissions.observationLabel")}
                  </div>
                  <FormControl
                    componentClass="textarea"
                    name="observation"
                    disabled={!lastStatusRecordSelected}
                    value={actualStatusRecord.observation || ""}
                    autoComplete="off"
                    onChange={handleStatusFieldChange}
                  />
                </FormGroup>
                {(!!regulatorySubmission.medicalRecordNumber ||
                  (regulatorySubmission.patients &&
                    regulatorySubmission.patients.length > 0) ||
                  !!regulatorySubmission.patientNamesDisplay) && (
                  <FormGroup>
                    <div
                      style={{
                        display: "inline-block",
                        verticalAlign: "middle",
                        marginRight: "24px",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "bold",
                          fontSize: "14px",
                          marginBottom: "2px",
                        }}
                      >
                        {regulatorySubmission.patients?.length > 1
                          ? t("regulatorySubmissions.patientsLabel")
                          : t("regulatorySubmissions.patientLabel")}
                      </div>
                    </div>
                    <div
                      style={{
                        display: "inline-block",
                        verticalAlign: "middle",
                        minWidth: "200px",
                        maxWidth: "100%",
                      }}
                    >
                      {regulatorySubmission.patients?.length > 0 ? (
                        <ul
                          style={{
                            margin: 0,
                            paddingLeft: "1.25rem",
                            listStyleType: "disc",
                          }}
                        >
                          {regulatorySubmission.patients.map((p, idx) => {
                            const nip = p.numberInProtocol;
                            const mrn = p.medicalRecordNumber;
                            const primary =
                              nip ||
                              mrn ||
                              "";
                            const showMrn =
                              mrn &&
                              nip &&
                              String(mrn).trim() !== String(nip).trim();
                            return (
                              <li key={`${mrn || "p"}-${idx}`} style={{ marginBottom: 4 }}>
                                <span>{primary}</span>
                                {showMrn && (
                                  <span
                                    style={{
                                      color: "#6c757d",
                                      marginLeft: 6,
                                      fontSize: "13px",
                                    }}
                                  >
                                    ({mrn})
                                  </span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <input
                          className="form-control"
                          value={
                            regulatorySubmission.patientNamesDisplay ||
                            regulatorySubmission.numberInProtocol ||
                            regulatorySubmission.patientName ||
                            ""
                          }
                          readOnly={true}
                          disabled={true}
                        />
                      )}
                    </div>
                  </FormGroup>
                )}
                {lastStatusRecordSelected && (
                  <FormGroup style={{ marginTop: "10px" }}>
                    {!saving && (
                      <>
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={saving}
                          onClick={() => save()}
                        >
                          <Save />
                          {t("clinicalTrials.general.save")}
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={saving}
                          onClick={() => {
                            setShowEditModal(true);
                          }}
                          style={{ marginLeft: "10px" }}
                        >
                          <IconSVG name="upload" />
                          {t(
                            "regulatorySubmissions.updateRegulatoryAttachment"
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={saving}
                          onClick={() => {
                            setShowAddCategoryModal(true);
                          }}
                          style={{ marginLeft: "10px" }}
                        >
                          <IconSVG name="Plus" />
                          {t(
                            "regulatorySubmissions.createRegulatoryAttachmentCategoria"
                          )}
                        </button>
                      </>
                    )}
                    {saving && <Spinner />}
                  </FormGroup>
                )}
              </div>
            }
          />
          {actualStatusRecord && (
            <>
              {/* attachments con Group */}
              {(() => {
                const grouped = actualStatusRecord.attachments
                  .filter((att) => att.groupId != null)
                  .reduce((acc, att) => {
                    if (!acc[att.groupId]) {
                      acc[att.groupId] = {
                        groupName: att.groupName,
                        items: [],
                      };
                    }
                    acc[att.groupId].items.push(att);
                    return acc;
                  }, {});

                return Object.values(grouped).map((group, gidx) => (
                  <Card
                    key={`group-${gidx}`}
                    content={
                      <>
                        <Row>
                          <Col md={12}>
                            <div className="header" style={{ padding: 0 }}>
                              <h4 className="title">{group.groupName}</h4>
                              <hr />
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          {group.items.map((att, idx2) => (
                            <Col md={6} key={idx2}>
                              <DocumentUploadCard
                                attachmentEntityTypeId={
                                  att.attachmentEntityTypeId
                                }
                                entityId={getAttachmentEntityId(att)}
                                attachment={att}
                                legend={"Subir documento"}
                                showDownloadButton={false}
                                disabled={
                                  !lastStatusRecordSelected ||
                                  !isAuthorized(["Startups.Edit"])
                                }
                                onAttachmentChange={handleAttachmentChange}
                                protocolId={regulatorySubmission.protocolId}
                              />
                              {att.patientName && (
                                <p>
                                  {t(
                                    "regulatorySubmissions.item.attachmentPatientLabel"
                                  )}{" "}
                                  {att.patientName}
                                </p>
                              )}
                              {att.practitionerName && (
                                <p>
                                  {t(
                                    "regulatorySubmissions.item.attachmentPractitionerLabel"
                                  )}{" "}
                                  {att.practitionerName}
                                </p>
                              )}
                            </Col>
                          ))}
                        </Row>
                      </>
                    }
                  />
                ));
              })()}

              {/* attachments con Category sin Group */}
              {(() => {
                const categorized = actualStatusRecord.attachments.filter(
                  (att) =>
                    att.groupId == null && att.attachmentCategoryId != null
                );

                const categorizedByTwo = groupArrayByTwo(categorized);

                return categorizedByTwo.map((row, idx3) => (
                  <Card
                    key={`category-${idx3}`}
                    content={
                      <Row>
                        {row.map((att, idx4) => (
                          <Col md={6} key={idx4}>
                            {!!att.practitionerName && (
                              <div>
                                <strong
                                  style={{
                                    display: "block",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {t(
                                    "regulatorySubmissions.cardPractitionerLabel"
                                  )}
                                </strong>
                                <div className="text-muted mb-2">
                                  {att.practitionerName}
                                </div>
                              </div>
                            )}
                            {!!att.patientName && (
                              <div>
                                <strong
                                  style={{
                                    display: "block",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {t("regulatorySubmissions.cardPatientLabel")}
                                </strong>
                                <div className="text-muted mb-2">
                                  {att.patientName}
                                </div>
                              </div>
                            )}
                            <div>
                              <DocumentUploadCard
                                attachmentEntityTypeId={
                                  att.attachmentEntityTypeId
                                }
                                entityId={getAttachmentEntityId(att)}
                                attachment={att}
                                showDownloadButton={false}
                                disabled={
                                  !lastStatusRecordSelected ||
                                  !isAuthorized(["Startups.Edit"])
                                }
                                onAttachmentChange={handleAttachmentChange}
                                protocolId={regulatorySubmission.protocolId}
                              />
                            </div>
                          </Col>
                        ))}
                      </Row>
                    }
                  />
                ));
              })()}

              {/* attachments sin Group ni Category */}
              {(() => {
                const uncategorized = actualStatusRecord.attachments.filter(
                  (att) =>
                    att.groupId == null && att.attachmentCategoryId == null
                );

                const uncategorizedByTwo = groupArrayByTwo(uncategorized);

                return uncategorizedByTwo.map((row, idx5) => (
                  <Card
                    key={`uncategorized-${idx5}`}
                    content={
                      <Row>
                        {row.map((att, idx6) => (
                          <Col md={6} key={idx6}>
                            {!!att.practitionerName && (
                              <div>
                                <strong
                                  style={{
                                    display: "block",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {t(
                                    "regulatorySubmissions.cardPractitionerLabel"
                                  )}
                                </strong>
                                <div className="text-muted mb-2">
                                  {att.practitionerName}
                                </div>
                              </div>
                            )}
                            {!!att.patientName && (
                              <div>
                                <strong
                                  style={{
                                    display: "block",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {t("regulatorySubmissions.cardPatientLabel")}
                                </strong>
                                <div className="text-muted mb-2">
                                  {att.patientName}
                                </div>
                              </div>
                            )}
                            <div>
                              <DocumentUploadCard
                                attachmentEntityTypeId={
                                  att.attachmentEntityTypeId
                                }
                                entityId={getAttachmentEntityId(att)}
                                attachment={att}
                                showDownloadButton={false}
                                disabled={
                                  !lastStatusRecordSelected ||
                                  !isAuthorized(["Startups.Edit"])
                                }
                                onAttachmentChange={handleAttachmentChange}
                                protocolId={regulatorySubmission.protocolId}
                              />
                            </div>
                          </Col>
                        ))}
                      </Row>
                    }
                  />
                ));
              })()}
            </>
          )}
          {showEditModal && (
            <ModalEditRegulatoryEvent
              protocolId={regulatorySubmission.protocolId}
              regulatorySubmissionId={regulatorySubmission.id}
              show={showEditModal}
              onClose={() => setShowEditModal(false)}
              onSave={onEditSave}
              regulatorySubmissionAttachments={actualStatusRecord.attachments}
              currentMedicalRecordNumber={
                regulatorySubmission.medicalRecordNumber
              }
            />
          )}
          {showAddCategoryModal && (
            <ModalCreateRegulatoryCategory
              show={showAddCategoryModal}
              onClose={() => setShowAddCategoryModal(false)}
              onSave={onAddCategorySave}
            />
          )}
        </>
      )}
    </>
  );
};

export const RegulatoryFiles = () => (
  <AttachmentSlotPollingProvider intervalMs={3000}>
    <RegulatoryFilesContent />
  </AttachmentSlotPollingProvider>
);

export default RegulatoryFiles;
